import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import './index.css'
import App from './App.tsx'
import { initTourDevTools } from './lib/tours/tourDevTools'

// ─── Sentry Error Tracking ────────────────────────────────────
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE, // 'development' | 'production'
    release: `seq-claims-app@${import.meta.env.VITE_APP_VERSION || '0.0.0'}`,

    // Performance: sample 10% of transactions in prod, 100% in dev
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

    // Session replay disabled (free tier optimization)
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,

    // Filter out noisy errors
    beforeSend(event) {
      // Ignore ResizeObserver loop errors (benign browser noise)
      if (event.exception?.values?.[0]?.value?.includes('ResizeObserver loop')) {
        return null;
      }
      // Ignore browser extension noise
      if (event.exception?.values?.[0]?.value?.includes('message channel closed')) {
        return null;
      }
      return event;
    },

    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: false }),
    ],
  });

  // SEQ-192: the report widget attaches the latest Sentry event id to a report
  // by calling window.Sentry.lastEventId(). Expose only that one function, not
  // the whole SDK.
  window.Sentry = { lastEventId: () => Sentry.lastEventId() };

  // Expose Sentry globally for dev testing:
  //   window.__sentry.captureMessage("test!")
  //   window.__sentry.captureException(new Error("boom"))
  if (import.meta.env.DEV) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__sentry = Sentry;
    console.log(
      '%c🛡️ Sentry loaded, test via: window.__sentry.captureMessage("Hello Sentry!")',
      'color: #8b5cf6; font-weight: bold;'
    );
  }
}

// ─── Tour Dev Tools ──────────────────────────────────────────────
initTourDevTools();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '2rem',
          textAlign: 'center',
          background: 'var(--bg-primary, #0a0a0f)',
          color: 'var(--text-primary, #e2e8f0)',
          fontFamily: 'Inter, system-ui, sans-serif',
        }}>
          <div style={{
            fontSize: '3rem',
            marginBottom: '1rem',
          }}>⚠️</div>
          <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.5rem' }}>
            Something went wrong
          </h2>
          <p style={{
            margin: '0 0 1.5rem',
            color: 'var(--text-muted, #94a3b8)',
            maxWidth: '400px',
          }}>
            An unexpected error occurred. The issue has been reported automatically.
          </p>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={resetError}
              style={{
                padding: '0.625rem 1.25rem',
                borderRadius: '8px',
                border: 'none',
                background: 'var(--color-primary, #3b82f6)',
                color: '#fff',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Try Again
            </button>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '0.625rem 1.25rem',
                borderRadius: '8px',
                border: '1px solid var(--border-color, #1e293b)',
                background: 'transparent',
                color: 'var(--text-primary, #e2e8f0)',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Reload Page
            </button>
          </div>
          {import.meta.env.DEV && error != null && (
            <pre style={{
              marginTop: '2rem',
              padding: '1rem',
              background: 'rgba(239,68,68,0.08)',
              borderRadius: '8px',
              border: '1px solid rgba(239,68,68,0.2)',
              fontSize: '0.75rem',
              textAlign: 'left',
              maxWidth: '600px',
              overflow: 'auto',
              color: '#f87171',
            }}>
              {String(error)}
            </pre>
          )}
        </div>
      )}
      showDialog
    >
      <App />
    </Sentry.ErrorBoundary>
  </StrictMode>,
)
